import React, { useEffect, useState } from 'react';
import { Button, DialogActions, DialogContent, Dialog, DialogContentText, CircularProgress, DialogTitle, styled, Typography, ButtonBase, Table, TableHead, TableCell, TableRow, TableBody, Container, Checkbox } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import { StorageScheme, Progress, DialogProviderContextModel } from 'models';
import * as DialogFactory from 'services/Dialog.Factory';
import { DialogProviderContext } from '../DialogProvider';
import * as ImageStore from '../../store/Image';
import { useAppDispatch } from '../../store';
import { getFriendlySize } from 'utils';
import classnames from 'classnames'

const TWO_MEGABYTES_AS_BYTES = 2000000;
const FIFTY_MEGABYTES_AS_BYTES = 50000000;

const Center = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
});

const FieldLabel = styled(Typography)(({ theme }) => ({
    textAlign: 'left',
    marginBottom: 12,
    marginLeft: 4
}));

const StorageSchemeName = styled(Typography)(({ theme }) => ({
    textAlign: 'left',
    margin: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
}));

const StorageSchemeDescription = styled(Typography)(({ theme }) => ({
    fontSize: '0.95em',
    textAlign: 'left',
    margin: 4
}));

const StorageSchemeButton = styled(ButtonBase)(({ theme }) => ({
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.grey[400],
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 18,
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 8,

    '&:hover': {
        borderColor: theme.palette.primary.light,
        backgroundColor: theme.palette.grey[100]
    },

    '&:not(.selected) .title, &:not(.selected) .description, &:not(.selected) .icon': {
        color: theme.palette.grey[500]
    },

    '&.selected': {
        borderColor: theme.palette.primary.main,
        '& .title, & .description, & .icon': {
            color: theme.palette.primary.dark
        }
    }
}));

const StyledErrorList = styled('ul')({
    listStyle: 'none',
    padding: 0,
    '& > li': {
        padding: 0,
        color: 'error'
    }
});

const StyledMenuBookIcon = styled(MenuBookIcon)(({ theme }) => ({
    marginRight: 4,
    color: theme.palette.grey[600]
}));

const StyledPublicIcon = styled(PublicIcon)(({ theme }) => ({
    marginRight: 6,
    color: theme.palette.grey[600]
}));

const FileNameBox = styled('div')({
    display: 'flex',
    alignItems: 'center',
    padding: 0
});

const ErrorBox = styled('div')(({ theme }) => ({
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    borderRadius: 2,
    padding: 8,
    margin: 8,
    marginTop: 32
}));

const FileTableRow = styled(TableRow)(({ theme }) => ({
    '&.error td, &.error td svg': {
        color: theme.palette.error.main
    },
    '& .strike': {
        textDecoration: 'line-through'
    }
}));

export interface FileValidationResult {
    file: File;
    error: string;
}

export interface UploadDialogProps {
    files: FileList;
    onClose: () => void;
    dialogContext: DialogProviderContextModel;
    folderId: string;
};

export function UploadDialog(props: UploadDialogProps) {
    const dispatch = useAppDispatch();
    const { files } = props;
    const [storageScheme, setStorageScheme] = useState<StorageScheme>(StorageScheme.Embedded);
    const [validationResults, setValidationResults] = useState<FileValidationResult[]>([]);
    const [skipFiles, setSkipFiles] = useState<File[]>([]);
    const [uploadError, setUploadError] = useState<string | null>();

    useEffect(() => {
        validate();
        setUploadError(null);
    }, [files, storageScheme]);

    function handleUpdateStorageScheme(scheme: StorageScheme) {
        setStorageScheme(scheme);
        validate();
    }

    const handleFileCheckChange = (file: File) => (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (checked) {
            handleUnskipFile(file);
        } else {
            handleSkipFile(file);
        }
    }

    function handleSkipFile(file: File) {
        if (skipFiles.indexOf(file) < 0) {
            setSkipFiles([...skipFiles, file]);
        }
    }

    function handleUnskipFile(file: File) {
        if (skipFiles.indexOf(file) >= 0) {
            setSkipFiles(skipFiles.filter(f => f !== file));
        }
    }

    function handleClose() {
        if (props.onClose) {
            props.onClose();
        }
    }

    function handleConfirm() {
        // make user clear errors first
        setUploadError(null);

        if (!validate(true)) {
            setUploadError('Please address the file errors above before continuing.');
            return;
        }

        const filesToUpload = getUnskippedFiles();
        const totalImages = filesToUpload.length;
        const getProgress = getProgressBuilder(totalImages);
        const [dialog, updateDialog] = DialogFactory.buildUploadProgressDialog(props.dialogContext, getProgress(false));
        dialog.open();

        dispatch(ImageStore.actionCreators.upload(storageScheme, props.folderId, filesToUpload, () => {
            updateDialog(getProgress(true));
        }, (error: string) => {
            updateDialog(getProgress(true, [error]));
        }));
    }

    const getProgressBuilder = (totalImages: number) => (complete: boolean, errors?: string[]) => {
        if (errors && errors.length > 0) {
            // error
            return {
                isComplete: true,
                status: <StyledErrorList>{errors.map(error => <li key={error}>{error}</li>)}</StyledErrorList>
            } as Progress;
        }

        if (complete) {
            // success
            return {
                isComplete: true,
                status: <div>{totalImages} {totalImages === 1 ? 'Image' : 'Images'} uploaded successfully</div>
            } as Progress;
        }

        // in progress
        return {
            isComplete: false,
            status: <div>Uploading {totalImages === 1 ? 'image' : 'images'}...</div>
        } as Progress;
    }

    const getFiles = (): File[] => {
        const uploadFiles = [] as File[];
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            if (file) {
                uploadFiles.push(file);
            }
        }
        return uploadFiles;
    }

    const getUnskippedFiles = (): File[] => {
        const allFiles = getFiles();
        return allFiles.filter(f => skipFiles.indexOf(f) < 0);
    }

    function validate(unskippedFilesOnly: boolean = false): boolean {
        const results = [] as FileValidationResult[];
        const filesToValidate = unskippedFilesOnly ? getUnskippedFiles() : getFiles();
        filesToValidate.forEach(file => {
            // make sure the file is not too big
            if (storageScheme === StorageScheme.Embedded && file.size > TWO_MEGABYTES_AS_BYTES) {
                results.push({ file, error: '2 MB image max exceeded' });
            } else if (storageScheme === StorageScheme.Independent && file.size > FIFTY_MEGABYTES_AS_BYTES) {
                results.push({ file, error: '50 MB image max exceeded' });
            }
        });
        setValidationResults(results);
        return results.length < 1;
    }

    return (
        <Dialog open={true} fullWidth>
            <DialogTitle>Upload Images</DialogTitle>
            <DialogContent>
                <Center>
                    <div>
                        <FieldLabel>Storage Scheme</FieldLabel>
                        <StorageSchemeButton className={storageScheme === StorageScheme.Embedded ? 'selected' : ''} onClick={() => handleUpdateStorageScheme(StorageScheme.Embedded)}>
                            <StorageSchemeName className='title'><StyledMenuBookIcon className='icon' /> Product Catalog Embedded (up to 2 MB per image)</StorageSchemeName>
                            <StorageSchemeDescription className='description'>This is suitable for images that need to be included in the product catalog (i.e. a swatch, room scene image, etc.)</StorageSchemeDescription>
                        </StorageSchemeButton>
                        <StorageSchemeButton className={storageScheme === StorageScheme.Independent ? 'selected' : ''} onClick={() => handleUpdateStorageScheme(StorageScheme.Independent)}>
                            <StorageSchemeName className='title'><StyledPublicIcon className='icon' /> Independent (up to 50 MB per image)</StorageSchemeName>
                            <StorageSchemeDescription className='description'>This should be used for images that do not need to be embedded in the product catalog (i.e. roomvo image, etc.). This is a suitable choice for images that merely need a place to be hosted publicly but are not referenced by any part of the product catalog.</StorageSchemeDescription>
                        </StorageSchemeButton>
                    </div>
                </Center>
                <br />
                <div>
                    <FieldLabel>Images</FieldLabel>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>File</TableCell>
                                <TableCell style={{minWidth: 70}}>Size</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getFiles().map(file => {
                                    const validationResult = validationResults.find(r => r.file === file);
                                    const isFileSkipped = skipFiles.includes(file);
                                    return <FileTableRow className={classnames({'error': !!validationResult})}>
                                        <TableCell padding="checkbox">
                                            <Checkbox color="primary" checked={!isFileSkipped} onChange={handleFileCheckChange(file)} />
                                        </TableCell>
                                        <TableCell>
                                            <FileNameBox>
                                                {storageScheme === StorageScheme.Embedded ? <StyledMenuBookIcon /> : <StyledPublicIcon />} 
                                                <span className={classnames({'strike': isFileSkipped})}>{file.name}</span>
                                            </FileNameBox>
                                        </TableCell>
                                        <TableCell className={classnames({'strike': isFileSkipped})}>{getFriendlySize(file.size)}</TableCell>
                                        <TableCell className={classnames({'strike': isFileSkipped})}>{validationResult?.error ?? ''}</TableCell>
                                    </FileTableRow> 
                                }
                            )}
                        </TableBody>
                        
                    </Table>
                </div>
                {uploadError && <ErrorBox><Typography>{uploadError}</Typography></ErrorBox>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleConfirm} color="primary">
                    Upload
                </Button>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    )
}